import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface DashboardStats {
  organizations: number;
  job_templates: number;
  total_interviews: number;
  active_interviews: number;
  completed_interviews: number;
  total_reports: number;
}

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Overview of your interview management system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Organizations</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.organizations || 0}</p>
            </div>
            <div className="text-4xl">🏢</div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Job Templates</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.job_templates || 0}</p>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Interviews</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.total_interviews || 0}</p>
            </div>
            <div className="text-4xl">🎤</div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Interviews</p>
              <p className="text-3xl font-bold text-blue-600">{stats?.active_interviews || 0}</p>
            </div>
            <div className="text-4xl">🟢</div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed Interviews</p>
              <p className="text-3xl font-bold text-green-600">{stats?.completed_interviews || 0}</p>
            </div>
            <div className="text-4xl">✅</div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Reports Generated</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.total_reports || 0}</p>
            </div>
            <div className="text-4xl">📄</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/organizations"
            className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
          >
            <div className="text-2xl">🏢</div>
            <div>
              <p className="font-medium text-gray-900 group-hover:text-blue-700">Manage Organizations</p>
              <p className="text-sm text-gray-600">Add or edit organizations</p>
            </div>
          </Link>

          <Link
            to="/admin/job-templates"
            className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group"
          >
            <div className="text-2xl">📋</div>
            <div>
              <p className="font-medium text-gray-900 group-hover:text-green-700">Job Templates</p>
              <p className="text-sm text-gray-600">Create role-specific templates</p>
            </div>
          </Link>

          <Link
            to="/admin/interviews"
            className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors group"
          >
            <div className="text-2xl">🎤</div>
            <div>
              <p className="font-medium text-gray-900 group-hover:text-purple-700">View Interviews</p>
              <p className="text-sm text-gray-600">Monitor interview sessions</p>
            </div>
          </Link>

          <Link
            to="/admin/reports"
            className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors group"
          >
            <div className="text-2xl">📄</div>
            <div>
              <p className="font-medium text-gray-900 group-hover:text-orange-700">View Reports</p>
              <p className="text-sm text-gray-600">Access candidate reports</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-800 font-medium">API Service</span>
            </div>
            <span className="text-green-600 text-sm">Online</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-800 font-medium">Database</span>
            </div>
            <span className="text-green-600 text-sm">Connected</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-blue-800 font-medium">AI Service</span>
            </div>
            <span className="text-blue-600 text-sm">Ready</span>
          </div>
        </div>
      </div>
    </div>
  );
};